
import React, { useEffect, useState } from 'react';
import { TestDataItem } from './types';
import type { FormValues } from '@/lib/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Youtube, Trash2, FileText, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    buildIntakeExplanationPayload,
    type IntakeCardType,
    type IntakeGoalBandState,
    type IntakeLateralizationState,
    type IntakeLegendTone,
} from '@/components/sop-intake/sop-analyzer/lateralization-explainer';
import { useSettings } from '@/components/settings/lib/settings-context';
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';
import { getSimpleIntakeLateralizationState } from '@/components/sop-intake/sop-analyzer/engine/table-spectrum';
import { sanitizeSignedNumericInput, signedNumericInputMode } from '@/components/sop-intake/lib/signed-numeric-input';
import { classifyMeasurementVersusGoalDeg } from '@/components/sop-intake/lib/sop-intake-goal-band';

// Custom Goniometer Icon Component
// Based on medical goniometer design from The Noun Project
// Icon #304703 by John Hart - https://thenounproject.com/icon/goniometer-304703/
const GoniometerIcon = ({ className }: { className?: string }) => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {/* Semi-circular protractor base */}
        <path d="M4 14 C4 8.5 7.5 4 12 4 C16.5 4 20 8.5 20 14" strokeWidth="2" />

        {/* Horizontal baseline */}
        <line x1="4" y1="14" x2="20" y2="14" strokeWidth="2" />

        {/* Center pivot point */}
        <circle cx="12" cy="14" r="1.5" fill="currentColor" />

        {/* Fixed measurement arm (left) */}
        <line x1="12" y1="14" x2="5" y2="14" strokeWidth="2.5" />

        {/* Movable measurement arm (angled) */}
        <line x1="12" y1="14" x2="8" y2="8" strokeWidth="2.5" />

        {/* Degree markings on arc - left side */}
        <line x1="6" y1="12" x2="5.5" y2="11" strokeWidth="1" />
        <line x1="8" y1="9" x2="7.5" y2="8" strokeWidth="1" />
        <line x1="10" y1="7" x2="9.5" y2="6" strokeWidth="1" />

        {/* Degree markings on arc - right side */}
        <line x1="14" y1="7" x2="14.5" y2="6" strokeWidth="1" />
        <line x1="16" y1="9" x2="16.5" y2="8" strokeWidth="1" />
        <line x1="18" y1="12" x2="18.5" y2="11" strokeWidth="1" />

        {/* Center top marking */}
        <line x1="12" y1="5" x2="12" y2="4" strokeWidth="1" />
    </svg>
);

import { Button } from '@/components/ui/button';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface TestInputCardProps {
    testKey: string;
    testData: TestDataItem;
    formValues: FormValues;
    onInputChange: (id: string, value: string) => void;
    onOpenVisioMeter?: (testKey: string, side: 'L' | 'R') => void;
}


const TestInputCard: React.FC<TestInputCardProps> = ({ testKey, testData, formValues, onInputChange, onOpenVisioMeter }) => {
    const { settings } = useSettings();
    const ruleDocVersion: RuleDocVersion = settings.useLegacySopAssessment ? 'legacy' : 'current';
    const procedureVariantFieldKey = `${testKey}_procedureVariant`;
    const defaultProcedureVariantId =
        testData.defaultProcedureVariant ?? testData.procedureVariants?.[0]?.id ?? 'standing';
    const storedProcedureVariantId =
        typeof formValues[procedureVariantFieldKey] === 'string'
            ? String(formValues[procedureVariantFieldKey])
            : defaultProcedureVariantId;
    const [procedureVariantId, setProcedureVariantId] = useState(storedProcedureVariantId);

    useEffect(() => {
        if (storedProcedureVariantId !== procedureVariantId) {
            setProcedureVariantId(storedProcedureVariantId);
        }
    }, [storedProcedureVariantId, procedureVariantId]);

    const handleProcedureVariantChange = (variantId: string) => {
        setProcedureVariantId(variantId);
        onInputChange(procedureVariantFieldKey, variantId);
    };
    const activeProcedureVariant =
        testData.procedureVariants?.find((variant) => variant.id === procedureVariantId) ??
        testData.procedureVariants?.[0];
    const displayVideoId = activeProcedureVariant?.videoId ?? testData.videoId;
    const hasVideo = !!displayVideoId;
    const isGroundingCard = testData.type === 'grounding';
    const isStandardBilateralCard = testKey !== 'FootPresentation' && testKey !== 'InfrasternalAngle' && testData.type !== 'text';
    const [notesSide, setNotesSide] = useState<'L' | 'R' | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isTitlePopoverOpen, setIsTitlePopoverOpen] = useState(false);
    const titlePopoverId = `${testKey}-explanation-popover`;

    const handleDeleteImage = (side: 'L' | 'R') => {
        const imageKey = `${side}_${testKey}_image`;
        onInputChange(imageKey, ''); // Clear the image data
        setShowDeleteConfirm(false);
        // keep notes dialog open if present; image viewer is deprecated
    };

    const getInputGoalBand = (value: any): IntakeGoalBandState => {
        if (value === '' || value === null || value === undefined || value === 'Select...') return 'none';
        if (testData.type === 'grounding' || testData.type === 'select' || testData.type === 'text' || testKey === 'FootPresentation') return 'none';

        const nominalVal = parseFloat(testData.nominal as string);
        const currentValue = parseFloat(String(value));
        if (isNaN(currentValue) || isNaN(nominalVal)) return 'none';

        const tier = classifyMeasurementVersusGoalDeg(currentValue, nominalVal);
        if (tier === 'hypermobile') return 'hypermobile';
        if (tier === 'below-goal') return 'below-goal';
        return 'near-goal';
    };

    const getInputHighlightClass = (value: any): string => {
        const status = getInputGoalBand(value);
        if (status === 'near-goal') return 'bg-status-clear-surface text-status-clear-text border-status-clear';
        if (status === 'below-goal') return 'bg-status-deviation-surface text-status-deviation-text border-status-deviation';
        if (status === 'hypermobile') return 'bg-status-warn-surface text-status-warn-text border-status-warn';
        return 'bg-bg text-fg border-line';
    };

    const sharedInputClasses = "w-full text-center bg-bg rounded-md border border-line focus:ring-2 focus:ring-accent focus:outline-none h-16 sm:h-20 text-lg xs:text-xl sm:text-3xl md:text-4xl font-extrabold transition-colors duration-200";

    // Numeric parsing and lateralization (component-level so wrapper can use it)
    const parseSideValue = (key: string): number | null => {
        if (testData.type === 'grounding') {
            const mode = (formValues.GroundingMode as string) === 'perceived' ? 'perceived' : 'dynamometer';
            const side = key.startsWith('L_') ? 'L' : 'R';
            const actual = formValues[`${side}_GroundingPerformance_actual`];
            const perceived = formValues[`${side}_GroundingPerformance_perceived`];
            const legacy = formValues[`${side}_GroundingPerformance`];
            const preferred = mode === 'perceived' ? perceived : actual;
            const secondary = mode === 'perceived' ? actual : perceived;
            const candidate = [preferred, secondary, legacy].find(
                (value) => value !== null && value !== undefined && value !== '' && value !== 'Select...'
            );
            if (candidate === null || candidate === undefined || candidate === '' || candidate === 'Select...') return null;
            const parsed = parseFloat(String(candidate));
            return isNaN(parsed) ? null : parsed;
        }
        const val = formValues[key];
        if (val === null || val === undefined || val === '' || val === 'Select...') return null;
        if (testData.type !== 'text' && testData.type !== 'select') {
            const parsed = parseFloat(String(val));
            return isNaN(parsed) ? null : parsed;
        }
        return null;
    };

    const leftValue = parseSideValue(`L_${testKey}`);
    const rightValue = parseSideValue(`R_${testKey}`);
    const footPresentationValue = testKey === 'FootPresentation' ? (formValues[testKey] as string | undefined) : undefined;
    const hasFootSelection = Boolean(footPresentationValue && footPresentationValue !== 'Select...');
    const getFootPresentationVisualState = () => {
        const raw = (footPresentationValue || '').toLowerCase();
        if (!raw || raw === 'select...' || raw === 'not sure') {
            return { inputClass: 'border-line', cardClass: '' };
        }
        if (raw.includes('r>l pronated')) {
            return { inputClass: 'border-status-warn bg-status-warn text-accent-fg', cardClass: 'border-accent border-2 bg-gradient-to-br from-accent/10 to-transparent' };
        }
        if (raw.includes('l>r pronated')) {
            return { inputClass: 'border-status-warn bg-status-warn text-accent-fg', cardClass: 'border-status-deviation border-2 bg-gradient-to-br from-status-deviation-surface/50 to-transparent' };
        }
        if (raw.includes('r>l supinated') || raw.includes('r supinated l pronated')) {
            return { inputClass: 'border-status-deviation bg-status-deviation text-accent-fg', cardClass: 'border-status-deviation border-2 bg-gradient-to-br from-status-deviation-surface/50 to-transparent' };
        }
        if (raw.includes('l>r supinated') || raw.includes('r pronated l supinated')) {
            return { inputClass: 'border-accent bg-accent/20', cardClass: 'border-accent border-2 bg-gradient-to-br from-accent/10 to-transparent' };
        }
        return { inputClass: 'border-line', cardClass: '' };
    };
    const footPresentationVisualState = getFootPresentationVisualState();
    const getIntakeLateralizationState = (): IntakeLateralizationState => {
        if (testData.type === 'grounding' || testKey === 'FootPresentation') {
            if (leftValue === null || rightValue === null) return 'none';
            if (leftValue === rightValue) return 'none';
            return leftValue > rightValue ? 'left' : 'right';
        }
        if (leftValue === null || rightValue === null) return 'none';
        
        const lateralization = getSimpleIntakeLateralizationState(testKey, leftValue, rightValue, ruleDocVersion);
        return lateralization;
    };

    const lateralization = getIntakeLateralizationState();
    const getExplanationCardType = (): IntakeCardType => {
        if (isGroundingCard) return 'grounding';
        if (testKey === 'FootPresentation') return 'foot-presentation';
        return 'numeric-bilateral';
    };
    const hasAnyCardValues = (): boolean => {
        if (testData.type === 'grounding') {
            return leftValue !== null || rightValue !== null;
        }
        if (testKey === 'FootPresentation') {
            return hasFootSelection;
        }
        if (testData.type === 'text') {
            return Boolean((formValues[`L_${testKey}`] as string) || (formValues[`R_${testKey}`] as string));
        }
        return leftValue !== null || rightValue !== null;
    };
    const getToneClass = (tone: IntakeLegendTone): string => {
        if (tone === 'left') return 'bg-accent';
        if (tone === 'right') return 'bg-status-deviation text-accent-fg';
        if (tone === 'balanced') return 'bg-status-clear text-accent-fg';
        if (tone === 'mixed') return 'bg-accent text-accent-fg';
        if (tone === 'goal') return 'bg-status-clear text-accent-fg';
        if (tone === 'warning') return 'bg-status-warn text-accent-fg';
        if (tone === 'info') return 'bg-accent text-accent-fg';
        return 'bg-bg';
    };
    const explanationPayload = buildIntakeExplanationPayload({
        testKey,
        testLabel: testData.label,
        cardType: getExplanationCardType(),
        leftValue,
        rightValue,
        nominalValue: typeof testData.nominal === 'number' ? testData.nominal : null,
        lateralizationState: lateralization,
        leftGoalBand: getInputGoalBand(formValues[`L_${testKey}`]),
        rightGoalBand: getInputGoalBand(formValues[`R_${testKey}`]),
    });
    const renderTitleWithPopover = (titleClassName = "text-base sm:text-lg md:text-xl font-semibold text-fg text-left truncate") => {
        const titleNode = (
            <CardTitle className={titleClassName}>
                {testData.label}
            </CardTitle>
        );
        if (!hasAnyCardValues()) return titleNode;

        return (
            <Popover open={isTitlePopoverOpen} onOpenChange={setIsTitlePopoverOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        aria-label={`Open explanation for ${testData.label}`}
                        aria-haspopup="dialog"
                        aria-expanded={isTitlePopoverOpen}
                        aria-controls={titlePopoverId}
                        onMouseEnter={() => setIsTitlePopoverOpen(true)}
                        onMouseLeave={() => setIsTitlePopoverOpen(false)}
                        onClick={() => setIsTitlePopoverOpen((prev) => !prev)}
                        onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                                setIsTitlePopoverOpen(false);
                            }
                        }}
                        className="min-w-0 text-left focus:outline-none focus:ring-2 focus:ring-accent rounded-sm"
                    >
                        {titleNode}
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    id={titlePopoverId}
                    align="start"
                    side="bottom"
                    className="w-[min(420px,90vw)] p-3"
                    onMouseEnter={() => setIsTitlePopoverOpen(true)}
                    onMouseLeave={() => setIsTitlePopoverOpen(false)}
                >
                    <div className="space-y-2">
                        <p className="text-sm font-semibold">{explanationPayload.headline}</p>
                        <p className="text-xs text-muted">{explanationPayload.decisionSummary}</p>
                        <p className="text-xs text-muted">Expected: {explanationPayload.expectedPattern}</p>
                        <p className="text-xs text-muted">{explanationPayload.biomechNote}</p>
                        <p className="text-xs text-muted">{explanationPayload.valueSummary}</p>
                        <div className="pt-1 border-t border-line">
                            <p className="text-xs font-semibold mb-1">Color legend</p>
                            <ul className="space-y-1">
                                {explanationPayload.legend.map((item) => (
                                    <li key={item.key} className="flex items-start gap-2">
                                        <span className={cn('mt-1 h-2.5 w-2.5 rounded-full', getToneClass(item.tone))} />
                                        <span className="text-[11px] text-muted">
                                            <span className="font-medium text-fg">{item.label}:</span> {item.description}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        );
    };
    const getGroundingInputHighlightClass = (side: 'L' | 'R') => {
        if (!isGroundingCard || leftValue === null || rightValue === null || leftValue === rightValue) {
            return 'border-line';
        }
        const winningSide = leftValue > rightValue ? 'L' : 'R';
        if (winningSide !== side) return 'border-line';
        return side === 'L' ? 'border-accent bg-accent/15' : 'border-status-deviation bg-status-deviation text-accent-fg';
    };

    const groundingMode = (formValues.GroundingMode as string) === 'perceived' ? 'perceived' : 'dynamometer';
    const renderTutorialButton = (buttonClass = "h-6 w-6 p-0", iconClass = "h-3 w-3") => (
        <Dialog>
            <DialogTrigger asChild>
                <Button
                    variant="default"
                    size="icon"
                    className={buttonClass}
                    title="Watch tutorial"
                >
                    <Play className={iconClass} />
                    <span className="sr-only">Watch tutorial</span>
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl" aria-describedby="test-tutorial-description">
                <DialogHeader>
                    <DialogTitle>{testData.label} Tutorial</DialogTitle>
                    <DialogDescription id="test-tutorial-description">
                        Learn how to properly perform the {testData.label} test
                    </DialogDescription>
                </DialogHeader>
                <div className="aspect-video rounded-lg overflow-hidden mt-4">
                    <LiteYouTubeEmbed id={displayVideoId!} title={testData.label} poster="maxresdefault" />
                </div>
            </DialogContent>
        </Dialog>
    );

    const renderHeaderGoniButton = () => {
        if (!onOpenVisioMeter || testData.type === 'select' || testData.type === 'grounding') return null;
        return (
            <Button
                variant="default"
                size="icon"
                className="h-6 w-6 p-0"
                onClick={() => onOpenVisioMeter(testKey, 'L')}
                aria-label="Open Visual Goniometer"
                title="Open Visual Goniometer"
            >
                <GoniometerIcon className="h-3 w-3" />
            </Button>
        );
    };

    const renderCardContent = () => {
        if (testKey === 'FootPresentation') {
            const hasStoredImage = Boolean(formValues[`L_${testKey}_image` as keyof FormValues]);
            return (
                <CardContent className="space-y-2 px-2 sm:px-4 md:px-6 py-4">
                    <div className="grid grid-cols-[auto_1fr] items-center gap-0">
                        <button
                            aria-label="Add notes for foot presentation"
                            className="h-[64px] w-9 sm:w-10 flex-shrink-0 flex flex-col items-center justify-center gap-1 border-2 rounded-l-lg border-r-0 border-accent/60 bg-bg hover:bg-bg dark:hover:bg-surface/80 transition-colors"
                            onClick={() => setNotesSide('L')}
                            title="Add notes"
                        >
                            <span className="text-lg sm:text-xl font-black leading-none text-accent">L</span>
                            <span className="relative inline-block">
                                <FileText className="h-3.5 w-3.5 text-accent/90" />
                                {hasStoredImage && (
                                    <Play className="absolute -top-1 -left-1 h-2.5 w-2.5 text-status-warn" />
                                )}
                            </span>
                        </button>
                        <div className={cn(
                            "rounded-r-lg rounded-l-none border-l-0 border-2 min-h-[64px] w-full overflow-hidden flex items-center justify-center px-2 py-1",
                            hasFootSelection ? footPresentationVisualState.inputClass : "border-line bg-bg"
                        )}>
                            <Select onValueChange={(value) => onInputChange(testKey, value)} value={formValues[testKey] as string || 'Select...'}>
                                <SelectTrigger
                                    className="w-full border-0 focus:ring-0 h-full text-base sm:text-lg font-semibold shadow-none rounded-none"
                                    style={{ backgroundColor: 'transparent', background: 'transparent' }}
                                >
                                    <SelectValue placeholder="Select..." style={{ backgroundColor: 'transparent' }} />
                                </SelectTrigger>
                                <SelectContent>
                                    {testData.options?.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            );
        }

        if (testKey === 'InfrasternalAngle') {
            const hasStoredImage = Boolean(formValues[`L_${testKey}_image` as keyof FormValues]);
            const value = formValues[testKey] as string || '';
            const numericVal = parseFloat(value);
            let classificationText = '';
            let classificationClass = '';
            if (Number.isFinite(numericVal)) {
                if (numericVal <= 90) {
                    classificationText = 'Narrow (ISA)';
                    classificationClass = 'bg-primary text-on-primary';
                } else {
                    classificationText = 'Wide (ISA)';
                    classificationClass = 'bg-error text-error-foreground';
                }
            }

            return (
                <CardContent className="space-y-2 px-2 sm:px-4 md:px-6 py-4">
                    <div className="grid grid-cols-[auto_1fr] items-center gap-0">
                        <button
                            aria-label="Add notes for infrasternal angle"
                            className="h-[64px] w-9 sm:w-10 flex-shrink-0 flex flex-col items-center justify-center gap-1 border-2 rounded-l-lg border-r-0 border-primary/60 bg-surface hover:bg-bg dark:hover:bg-bg transition-colors"
                            onClick={() => setNotesSide('L')}
                            title="Add notes"
                        >
                            <span className="relative inline-block">
                                <FileText className="h-3.5 w-3.5 text-primary/90" />
                                {hasStoredImage && (
                                    <Play className="absolute -top-1 -left-1 h-2.5 w-2.5 text-warning-text" />
                                )}
                            </span>
                        </button>
                        <div className="rounded-r-lg rounded-l-none border-l-0 border-2 min-h-[64px] w-full overflow-hidden flex items-center justify-center px-2 py-1 border-line bg-surface">
                            <Input
                                type="text"
                                inputMode="numeric"
                                enterKeyHint="done"
                                autoComplete="off"
                                spellCheck={false}
                                id={testKey}
                                value={value}
                                onChange={(e) => onInputChange(testKey, sanitizeSignedNumericInput(e.target.value))}
                                className="w-full text-center bg-transparent border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-full text-3xl font-extrabold p-0 text-fg placeholder:text-muted/50"
                                placeholder="90°"
                            />
                        </div>
                    </div>
                    {classificationText && (
                        <div className={cn("mt-1 min-h-8 rounded-md flex items-center justify-center px-3 font-black text-lg", classificationClass)}>
                            {classificationText}
                        </div>
                    )}
                </CardContent>
            );
        }

        if (testData.type === 'text') { // Legacy text type
            return (
                <CardContent className="space-y-2 px-2 sm:px-4 md:px-6 py-4">
                    <div className="text-center text-xs text-muted mb-2">
                        Goal: {testData.nominalDisplay || testData.nominal + (testData.unit || '')}
                    </div>
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 xs:gap-4 sm:gap-6 md:gap-10">
                        <div className="text-center min-w-0 w-full">
                            <label htmlFor={`L_${testKey}`} className="text-xs sm:text-sm font-semibold text-muted block mb-1">Left</label>
                            <div className="rounded-lg border-2 border-line bg-bg min-h-[100px] w-full overflow-hidden flex items-center justify-center">
                                <Input
                                    type="text"
                                    id={`L_${testKey}`}
                                    value={formValues[`L_${testKey}`] as string || ''}
                                    onChange={(e) => onInputChange(`L_${testKey}`, e.target.value)}
                                    className="w-full text-center bg-transparent border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-full text-4xl sm:text-5xl md:text-6xl font-extrabold p-0 text-inherit placeholder:text-muted/50"
                                    placeholder="Left..."
                                />
                            </div>
                        </div>
                        <div className="text-center text-xs sm:text-sm text-muted px-2">
                            {testData.unit || ''}
                        </div>
                        <div className="text-center min-w-0 w-full">
                            <label htmlFor={`R_${testKey}`} className="text-xs sm:text-sm font-semibold text-muted block mb-1">Right</label>
                            <div className="rounded-lg border-2 border-line bg-bg min-h-[100px] w-full overflow-hidden flex items-center justify-center">
                                <Input
                                    type="text"
                                    id={`R_${testKey}`}
                                    value={formValues[`R_${testKey}`] as string || ''}
                                    onChange={(e) => onInputChange(`R_${testKey}`, e.target.value)}
                                    className="w-full text-center bg-transparent border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-full text-4xl sm:text-5xl md:text-6xl font-extrabold p-0 text-inherit placeholder:text-muted/50"
                                    placeholder="Right..."
                                />
                            </div>
                        </div>
                    </div>
                </CardContent>
            );
        }

        // Standard two-sided input card
        const createInput = (side: 'L' | 'R') => {
            const id = `${side}_${testKey}`;
            const value = formValues[id];
            const inputGoalBand = getInputGoalBand(value);

            if (testData.type === 'grounding') {
                const perceivedOptions = Array.from({ length: 10 }, (_, i) => i + 1);
                const actualKey = `${id}_actual`;
                const perceivedKey = `${id}_perceived`;
                const legacyValue = formValues[id] as string | undefined;
                const actualValue = (formValues[actualKey] as string | undefined) || legacyValue || '';
                const perceivedValue = (formValues[perceivedKey] as string | undefined) || '';
                const activeValue = groundingMode === 'perceived' ? perceivedValue : actualValue;

                return (
                    <div
                        className={cn(
                            `border-2 ${getGroundingInputHighlightClass(side)} min-h-[76px] sm:min-h-[84px] w-full min-w-0 overflow-hidden flex items-center justify-center px-1.5 py-1`,
                            side === 'L' ? "rounded-r-lg rounded-l-none border-l-0" : "rounded-l-lg rounded-r-none border-r-0"
                        )}
                    >
                        {groundingMode === 'dynamometer' ? (
                            <Input
                                type="number"
                                min={0}
                                step={0.1}
                                id={actualKey}
                                value={actualValue}
                                onChange={(e) => onInputChange(actualKey, e.target.value)}
                                className="w-full text-center bg-transparent border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-full text-[2.75rem] sm:text-[3.25rem] md:text-[3.5rem] leading-none font-black tracking-tight p-0 text-fg placeholder:text-muted/50 [appearance:textfield] [font-variant-numeric:tabular-nums] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                placeholder="0-20"
                            />
                        ) : (
                            <Select
                                value={perceivedValue || 'placeholder'}
                                onValueChange={(selectedValue) => onInputChange(perceivedKey, selectedValue === 'placeholder' ? '' : selectedValue)}
                            >
                                <SelectTrigger
                                    className={cn(
                                        "w-full text-center bg-transparent border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-full text-[2rem] sm:text-[2.5rem] md:text-[2.75rem] font-extrabold p-0 [&>svg]:hidden",
                                        !perceivedValue ? "text-accent hover:text-accent/80" : "text-fg"
                                    )}
                                >
                                    <SelectValue placeholder="Select..." className="text-base sm:text-lg font-bold" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="placeholder">Select...</SelectItem>
                                    {perceivedOptions.map((option) => (
                                        <SelectItem key={option} value={option.toString()}>
                                            {option}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                );
            }

            if (testData.type === 'select') {
                return (
                    <div
                        className={cn(
                            'border-2 min-h-[76px] sm:min-h-[84px] w-full min-w-0 overflow-hidden flex items-center justify-center px-1.5 py-1',
                            getInputHighlightClass(value),
                            side === 'L' ? "rounded-r-lg rounded-l-none border-l-0" : "rounded-l-lg rounded-r-none border-r-0"
                        )}
                    >
                        <Select onValueChange={(v) => onInputChange(id, v)} value={value as string || undefined}>
                            <SelectTrigger className={cn(
                                "w-full text-center bg-transparent border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-full text-[2rem] sm:text-[2.5rem] md:text-[2.75rem] font-extrabold p-0 [&>svg]:hidden",
                                (!value || value === 'Select...') ? "text-accent hover:text-accent/80" : "text-inherit"
                            )}>
                                <SelectValue placeholder="Select..." className="text-base sm:text-lg font-bold" />
                            </SelectTrigger>
                            <SelectContent>
                                {testData.options?.map(o => (
                                    <SelectItem key={o} value={o}>
                                        <span className="max-w-[80vw] sm:max-w-none truncate">
                                            {o}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                );
            }
            return (
                <div
                    className={cn(
                        'border-2 min-h-[76px] sm:min-h-[84px] w-full min-w-0 overflow-hidden flex items-center justify-center px-1.5 py-1',
                        getInputHighlightClass(value),
                        side === 'L' ? "rounded-r-lg rounded-l-none border-l-0" : "rounded-l-lg rounded-r-none border-r-0"
                    )}
                >
                    <div className="relative w-full h-full flex items-center justify-center">
                        <Input
                            type="text"
                            inputMode={signedNumericInputMode(testData.allowsNegative)}
                            enterKeyHint="done"
                            autoComplete="off"
                            autoCorrect={testData.allowsNegative ? 'off' : undefined}
                            spellCheck={false}
                            id={id}
                            value={value as string || ''}
                            onChange={(e) => {
                                const next = e.target.value;
                                onInputChange(id, testData.allowsNegative ? sanitizeSignedNumericInput(next) : next);
                            }}
                            className="w-full text-center bg-transparent border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-full text-[2.75rem] sm:text-[3.25rem] md:text-[3.5rem] leading-none font-black tracking-tight p-0 text-inherit placeholder:text-muted/50 [appearance:textfield] [font-variant-numeric:tabular-nums] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            placeholder={testData.unit || ''}
                        />
                        {inputGoalBand === 'hypermobile' && (
                            <span
                                aria-label={`hypermobility-indicator-${side}`}
                                className="absolute top-0.5 right-1 text-status-warn text-lg sm:text-xl md:text-2xl font-black leading-none pointer-events-none"
                            >
                                ^
                            </span>
                        )}
                    </div>
                </div>
            );
        };

        const renderNotesButton = (side: 'L' | 'R') => {
            const hasStoredImage = Boolean(formValues[`${side}_${testKey}_image`]);
            const isLeftSide = side === 'L';
            return (
                <button
                    aria-label={`Add notes for ${side}`}
                    className={cn(
                        "h-[76px] sm:h-[84px] w-9 sm:w-10 flex-shrink-0 flex flex-col items-center justify-center gap-1 border-2 transition-colors",
                        "bg-bg hover:bg-bg dark:hover:bg-surface/80",
                        isLeftSide ? "rounded-l-lg border-r-0 border-accent/60" : "rounded-r-lg border-l-0 border-status-deviation/60"
                    )}
                    onClick={() => setNotesSide(side)}
                    title="Add notes"
                >
                    <span className={cn(
                        "text-lg sm:text-xl font-black leading-none",
                        isLeftSide ? "text-accent" : "text-status-deviation"
                    )}>
                        {side}
                    </span>
                    <span className="relative inline-block">
                        <FileText className={cn("h-3.5 w-3.5", isLeftSide ? "text-accent/90" : "text-status-deviation/90")} />
                        {hasStoredImage && (
                            <Play className="absolute -top-1 -left-1 h-2.5 w-2.5 text-status-warn" />
                        )}
                    </span>
                </button>
            );
        };

        return (
            <CardContent className="space-y-1 px-2 sm:px-3 md:px-4 py-3 sm:py-3.5 md:py-4">
                <div className="grid grid-cols-2 items-start gap-2 sm:gap-4">
                    <div className="min-w-0 w-full flex items-center">
                        <div className="flex items-stretch w-full">
                            {renderNotesButton('L')}
                            <div className="text-center min-w-0 flex-1">
                                {createInput('L')}
                            </div>
                        </div>
                    </div>

                    <div className="min-w-0 w-full flex items-center">
                        <div className="flex items-stretch w-full">
                            <div className="text-center min-w-0 flex-1">
                                {createInput('R')}
                            </div>
                            {renderNotesButton('R')}
                        </div>
                    </div>
                </div>
                {leftValue !== null && rightValue !== null && !isGroundingCard && lateralization !== 'none' && (
                    <div
                        aria-label="lateralization-indicator-bar"
                        className={cn(
                            "mt-1 sm:mt-1.5 min-h-8 sm:min-h-9 rounded-md flex items-center justify-center px-3",
                            lateralization === 'left' && "bg-accent text-accent-fg",
                            lateralization === 'right' && "bg-status-deviation text-accent-fg",
                            lateralization === 'balanced' && "bg-status-clear text-accent-fg",
                            lateralization === 'mixed' && "bg-accent text-accent-fg"
                        )}
                    >
                        <span
                            aria-label="lateralization-value"
                            className="text-lg sm:text-xl md:text-2xl font-black leading-none tracking-tight text-accent-fg"
                        >
                            {(() => {
                                if (lateralization === 'balanced') return 'Balanced';
                                const diff = Math.abs(leftValue - rightValue);
                                const safeDiff = Number.isFinite(diff) ? Math.round(diff) : 0;
                                if (lateralization === 'mixed') return `Mixed · ${safeDiff}°`;
                                if (lateralization === 'right') return `Right Lat. ${safeDiff} >`;
                                if (lateralization === 'left') return `< ${safeDiff} Left Lat.`;
                                return '';
                            })()}
                        </span>
                    </div>
                )}
            </CardContent>
        );
    };

    return (
        <>
            <Card className={cn(
                "h-full flex flex-col relative overflow-hidden bg-bg",
                testKey === 'FootPresentation' && hasFootSelection && footPresentationVisualState.cardClass,
                lateralization === 'left' && "border-accent border-2 bg-gradient-to-br from-accent/10 to-transparent",
                lateralization === 'right' && "border-status-deviation border-2 bg-gradient-to-br from-status-deviation-surface/50 to-transparent",
                lateralization === 'balanced' && "border-status-clear border-2 bg-gradient-to-br from-status-clear-surface/50 to-transparent",
                lateralization === 'mixed' && "border-accent border-2 bg-gradient-to-br from-accent/10 to-transparent from-accent/20"
            )}>
                <CardHeader className={cn(
                    "pb-2 sm:pb-2.5 md:pb-3",
                    isStandardBilateralCard ? "items-center text-center" : "flex flex-row items-start justify-between"
                )}>
                    <div className={cn(isStandardBilateralCard ? "w-full flex items-center justify-between gap-2 sm:gap-3" : "flex-1")}>
                        {isStandardBilateralCard ? (
                            <>
                                <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 min-w-0">
                                    {renderTitleWithPopover()}
                                    {isGroundingCard ? (
                                        <div className="flex h-6 sm:h-7 w-[150px] sm:w-[170px] flex-shrink-0 items-center overflow-hidden rounded border border-line bg-surface/30">
                                            <button
                                                type="button"
                                                aria-label="Grounding mode dynamometer"
                                                className={cn(
                                                    "flex h-full flex-1 items-center justify-center text-[10px] sm:text-xs font-bold transition-colors",
                                                    groundingMode === 'dynamometer'
                                                        ? "bg-accent text-accent-fg"
                                                        : "text-muted hover:bg-surface/60"
                                                )}
                                                onClick={() => onInputChange('GroundingMode', 'dynamometer')}
                                            >
                                                Dyno
                                            </button>
                                            <button
                                                type="button"
                                                aria-label="Grounding mode perceived"
                                                className={cn(
                                                    "flex h-full flex-1 items-center justify-center text-[10px] sm:text-xs font-bold transition-colors",
                                                    groundingMode === 'perceived'
                                                        ? "bg-accent text-accent-fg"
                                                        : "text-muted hover:bg-surface/60"
                                                )}
                                                onClick={() => onInputChange('GroundingMode', 'perceived')}
                                            >
                                                Perceived
                                            </button>
                                        </div>
                                    ) : testData.procedureVariants && testData.procedureVariants.length > 1 ? (
                                        <div className="flex h-6 sm:h-7 w-[150px] sm:w-[190px] flex-shrink-0 items-center overflow-hidden rounded border border-line bg-surface/30">
                                            {testData.procedureVariants.map((variant) => (
                                                <button
                                                    key={variant.id}
                                                    type="button"
                                                    aria-label={`Procedure variant ${variant.label}`}
                                                    className={cn(
                                                        'flex h-full flex-1 items-center justify-center px-1 text-[10px] sm:text-xs font-bold transition-colors',
                                                        procedureVariantId === variant.id
                                                            ? 'bg-accent text-accent-fg'
                                                            : 'text-muted hover:bg-surface/60'
                                                    )}
                                                    onClick={() => handleProcedureVariantChange(variant.id)}
                                                >
                                                    {variant.label}
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-[10px] sm:text-xs md:text-sm text-muted whitespace-nowrap">
                                            Goal: {testData.nominalDisplay || testData.nominal + (testData.unit || '')}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center flex-shrink-0">
                                    <div className="flex items-center gap-1">
                                        {hasVideo ? renderTutorialButton("h-6 w-6 p-0", "h-3 w-3") : null}
                                        {renderHeaderGoniButton()}
                                    </div>
                                </div>
                            </>
                        ) : (
                            renderTitleWithPopover("text-base font-semibold text-fg")
                        )}
                        {testData.optional && (
                            <span className="text-xs font-normal text-muted border border-muted rounded px-1 mt-0.5 self-start">Optional</span>
                        )}
                    </div>
                    {!isStandardBilateralCard && (
                        <div className="flex items-center gap-1 -mr-2 -mt-1 flex-shrink-0">
                            {testKey === 'FootPresentation' && testData.videos && testData.videos.length > 0 ? (
                                // Special handling for Foot Presentation - show 3 small red play buttons
                                testData.videos.map((video, index) => (
                                    <Dialog key={index}>
                                        <DialogTrigger asChild>
                                            <Button
                                                variant="default"
                                                size="icon"
                                                className="h-6 w-6 p-0"
                                                title={video.label}
                                            >
                                                <Play className="h-3 w-3" />
                                                <span className="sr-only">{video.label}</span>
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-4xl" aria-describedby="foot-presentation-demo-description">
                                            <DialogHeader>
                                                <DialogTitle>{video.label} - Foot Presentation Test</DialogTitle>
                                                <DialogDescription id="foot-presentation-demo-description">
                                                    Watch the demonstration for the {video.label} test
                                                </DialogDescription>
                                            </DialogHeader>
                                            <div className="aspect-video rounded-lg overflow-hidden mt-4">
                                                <LiteYouTubeEmbed
                                                    id={video.url.split('v=')[1]?.split('&')[0] || ''}
                                                    title={video.label}
                                                />
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                ))
                            ) : hasVideo ? (
                                renderTutorialButton()
                            ) : null}
                        </div>
                    )}
                </CardHeader>
                <div className="flex-grow">
                    {renderCardContent()}
                </div>
            </Card>

            {/* Removed dedicated Image Viewer dialog in favor of unified Notes dialog */}

            {/* Notes Modal */}
            {notesSide && (
                <Dialog open={!!notesSide} onOpenChange={() => setNotesSide(null)}>
                    <DialogContent className="max-w-xl" aria-describedby="notes-description">
                        <DialogHeader>
                            <DialogTitle>Notes - {notesSide === 'L' ? 'Left' : 'Right'}</DialogTitle>
                            <DialogDescription id="notes-description">
                                Add detailed notes for the {notesSide === 'L' ? 'left' : 'right'} side measurement. If an image was stored, it appears below.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="mt-2 space-y-4">
                            {formValues[`${notesSide}_${testKey}_image`] && (
                                <img
                                    src={formValues[`${notesSide}_${testKey}_image`] as string}
                                    alt={`${testData.label} ${notesSide} measurement`}
                                    className="w-full h-auto rounded-lg border border-line"
                                />
                            )}
                            <textarea
                                className="w-full min-h-[140px] rounded-md border border-line bg-bg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                                value={(formValues[`${notesSide}_${testKey}_notes`] as string) || ''}
                                onChange={(e) => onInputChange(`${notesSide}_${testKey}_notes`, e.target.value)}
                                placeholder="Add notes about this measurement..."
                            />
                            {formValues[`${notesSide}_${testKey}_image`] && (
                                !showDeleteConfirm ? (
                                    <div className="flex justify-end">
                                        <Button
                                            variant="destructive"
                                            onClick={() => setShowDeleteConfirm(true)}
                                            className="h-10"
                                        >
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            Delete Image
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="p-4 bg-status-deviation-surface/20 rounded-md border border-status-deviation">
                                        <p className="text-sm font-semibold text-status-deviation mb-3">
                                            Are you sure you want to delete this measurement image?
                                        </p>
                                        <div className="flex gap-3 justify-end">
                                            <Button
                                                variant="outline"
                                                onClick={() => setShowDeleteConfirm(false)}
                                                className="h-10"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                onClick={() => { handleDeleteImage(notesSide!); setShowDeleteConfirm(false); }}
                                                className="h-10"
                                            >
                                                <Trash2 className="h-4 w-4 mr-2" />
                                                Confirm Delete
                                            </Button>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                        <div className="mt-4 flex justify-end">
                            <Button variant="outline" className="h-10" onClick={() => setNotesSide(null)}>Close</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

export default TestInputCard;
